import React from 'react';
import { DayCounts, Overview } from '../../../api/cafAutomation';
import { formatCount, percent, shortDay } from '../format';
import { StackSeries, StackedDailyBars } from '../how/charts';
import { NOT_ENOUGH_FINISHED_EMAILS, REFRESH_FAILED, STATE_LABEL, STATE_TONE, WORKING_LOADING, periodPhrase, workingLoadFailed } from './automationCopy';

const WATCH_SERIES: StackSeries[] = [
  { key: 'auto', label: 'Other AI decisions', tone: 0 },
  { key: 'workflow', label: 'Ran a workflow', tone: 1 },
  { key: 'manual', label: 'Marked as manual', tone: 2 },
];
const REPLACEMENT_ON_SERIES: StackSeries[] = [
  { key: 'workflow', label: 'Workflow runs started', tone: 1 },
  { key: 'manual', label: 'Manual handling', tone: 2 },
];

const Legend: React.FC<{ series: StackSeries[] }> = ({ series }) => (
  <div className="cur-inline cur-small">
    {series.map((item) => (
      <span key={item.key} className="cur-inline"><span className={`cur-sw cur-sw-${item.tone}`} aria-hidden="true" />{item.label}</span>
    ))}
  </div>
);

const NumbersTable: React.FC<{ days: DayCounts[]; series: StackSeries[] }> = ({ days, series }) => (
  <div className="cur-table-wrap">
    <table className="cur-tbl" aria-label="Numbers per day">
      <thead>
        <tr><th>Day</th>{series.map((item) => <th key={item.key} className="cur-right">{item.label}</th>)}<th className="cur-right">Total</th></tr>
      </thead>
      <tbody>
        {days.map((day) => (
          <tr key={day.date}>
            <td>{shortDay(day.date)}</td>
            {series.map((item) => <td key={item.key} className="cur-right cur-tnum">{formatCount(day[item.key])}</td>)}
            <td className="cur-right cur-tnum">{formatCount(series.reduce((sum, item) => sum + day[item.key], 0))}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ByWorkflow: React.FC<{ overview: Overview; onOpenWorkflow: (id: string) => void }> = ({ overview, onOpenWorkflow }) => {
  const rows = overview.by_workflow;
  const max = Math.max(1, ...rows.map((row) => row.total));
  return (
    <div>
      <div className="cur-side-h" id="cur-by-workflow">{overview.evidence?.scope === 'caf-workflows-1' && overview.switch.mode === 'on'
        ? 'Runs started, by workflow' : 'Would have run, by workflow'}</div>
      {!rows.length && <p className="cur-hint">No workflow would have run yet.</p>}
      <ul className="cur-wf-rows" aria-labelledby="cur-by-workflow">
        {rows.map((row) => (
          <li key={row.workflow_id}>
            <div className="cur-wf-top">
              <button type="button" className="cur-linkish cur-ellip cur-grow" onClick={() => onOpenWorkflow(row.workflow_id)}>{row.name}</button>
              <strong className="cur-tnum">{formatCount(row.total)}</strong>
            </div>
            <div className="cur-wf-bar" aria-hidden="true"><i style={{ width: `${Math.max(3, (row.total / max) * 100)}%` }} /></div>
            <div className="cur-inline cur-small cur-muted">
              {row.state !== 'on' && <span className={`cur-pill cur-pill--${STATE_TONE[row.state]}`}>{STATE_LABEL[row.state]}</span>}
              <span>{`Used by ${row.used_by} ${row.used_by === 1 ? 'example' : 'examples'}`}</span>
            </div>
          </li>
        ))}
      </ul>
      {overview.by_workflow_more > 0 && (
        <p className="cur-hint">{`and ${overview.by_workflow_more} more in the Workflows list below`}</p>
      )}
    </div>
  );
};

const caption = (days: DayCounts[], watching: boolean) => (days.length
  ? `${watching ? 'What the AI would have done' : 'What the AI did'}, per day, ${shortDay(days[0].date)} to ${shortDay(days[days.length - 1].date)}. Hover a bar for the numbers.`
  : '');

export const WorkingCard: React.FC<{
  overview: Overview; onRequestWatching: () => void; onOpenWorkflow: (id: string) => void;
  /** The window the page has selected. While the data is for another window, no numbers are shown. */
  days: number;
  /** The last refresh failed; the data shown is the last good copy. */
  failed?: boolean;
  /** Try the failed load again. */
  onRetry: () => void;
}> = ({ overview, onRequestWatching, onOpenWorkflow, days, failed = false, onRetry }) => {
  const stale = overview.days !== days;
  const mode = overview.switch.mode;
  const { totals } = overview;
  const replacement = overview.evidence?.scope === 'caf-workflows-1';
  const series = mode === 'on' && replacement ? REPLACEMENT_ON_SERIES : mode === 'on' && !overview.capabilities?.runs
    ? WATCH_SERIES.filter((item) => item.key !== 'workflow')
    : WATCH_SERIES;
  const handled = totals.auto + totals.workflow;
  return (
    <section className="ui-card" aria-label="Is it working?">
      <div className="ui-card__header">
        <h2 className="ui-card__title">Is it working?</h2>
        <span className="cur-muted cur-small">{`Last ${days} days · the AI is ${mode === 'on' ? 'on' : mode === 'watching' ? 'watching' : 'off'}`}</span>
        {failed && !stale && <span className="cur-muted cur-small" role="status">{REFRESH_FAILED}</span>}
      </div>
      <div className="ui-card__body">
        {stale && failed ? (
          <div className="cur-empty" role="status">
            {workingLoadFailed(days)}
            <div style={{ marginTop: 10 }}>
              <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={onRetry}>Try again</button>
            </div>
          </div>
        ) : stale ? (
          <div className="cur-empty" role="status" aria-busy="true">{WORKING_LOADING}</div>
        ) : mode === 'off' ? (
          <div className="cur-empty">
            <strong>The AI is off.</strong>
            Lender emails are waiting in the Lender lane for staff.
            <div style={{ marginTop: 10 }}>
              <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" onClick={onRequestWatching}>Switch to watching…</button>
            </div>
          </div>
        ) : (
          <div className="cur-work-grid">
            <div className="cur-stack">
              {mode === 'watching' ? (
                totals.total ? (
                  <p className="cur-lead">
                    {replacement ? <>
                      The AI proposed workflow handling for <strong>{formatCount(totals.workflow)}</strong>{` of ${formatCount(totals.total)} lender emails ${periodPhrase(overview.days)}. Watching made no business changes; staff handled the tasks.`}
                    </> : <>
                      The AI would have handled <strong>{formatCount(handled)}</strong>{` of ${formatCount(totals.total)} lender emails ${periodPhrase(overview.days)} (${percent(handled, totals.total)}%). It did nothing; staff handled them all.`}
                    </>}
                  </p>
                ) : <p className="cur-lead">{`The AI hasn't read any lender emails ${periodPhrase(overview.days)} yet.`}</p>
              ) : (
                <div className="cur-kpis">
                  <div className="cur-kpi"><strong>{formatCount(replacement ? overview.execution?.started ?? 0 : totals.auto)}</strong><span>{replacement ? 'Workflow runs started' : 'Done automatically'}</span></div>
                  <div className="cur-kpi"><strong>{formatCount(totals.manual)}</strong><span>Marked as manual</span></div>
                </div>
              )}
              {mode === 'watching' && overview.matches_staff && (
                <div className="cur-match-box">
                  {overview.matches_staff.percent != null && <strong>{`${overview.matches_staff.percent}%`}</strong>}
                  <div>
                    <div><strong>Matches what staff did</strong></div>
                    <div className="cur-small">
                      {overview.matches_staff.percent != null
                        ? `Of the ${formatCount(overview.matches_staff.finished)} emails staff have finished, how often the AI would have done the same.`
                        : NOT_ENOUGH_FINISHED_EMAILS}
                    </div>
                  </div>
                </div>
              )}
              <Legend series={series} />
              <p className="cur-hint">{caption(overview.series, mode === 'watching')}</p>
              <StackedDailyBars days={overview.series} series={series}
                label={mode === 'watching' ? 'What the AI would have done, per day' : 'What the AI did, per day'} />
              <NumbersTable days={overview.series} series={series} />
            </div>
            <ByWorkflow overview={overview} onOpenWorkflow={onOpenWorkflow} />
          </div>
        )}
      </div>
    </section>
  );
};
